import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { publicApi } from '@l2u/web-shared';
import { SignInBrandLogo } from '@l2u/web-shared';
import { CenteredSpinner } from '@l2u/web-shared';
import { ErrorMessage } from '@l2u/web-shared';
import { PoweredByFooter } from '@l2u/web-shared';
import { AppVersion } from '@l2u/web-shared';
import { companyPath } from '../lib/audienceRoutes.ts';
import { isAccountLocked } from '../lib/lockedRedirect.ts';

export function CompanyLanding() {
  const { token = '' } = useParams();
  const [error, setError] = useState<unknown>(null);
  const ranRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    if (!token) {
      setError(new Error('Missing invite token'));
      return;
    }
    publicApi
      .post<{ action_link: string }>('/api/auth/company/redeem', { token })
      .then((body) => {
        if (!body?.action_link) throw new Error('Redeem returned no action link');
        window.location.href = body.action_link;
      })
      .catch((err) => {
        // A valid invitation for a LOCKED account: the api names the lock only
        // because the signed token proves who this is. Its own page, not the
        // dead-link one, whose "email me a new sign-in link" would loop.
        if (isAccountLocked(err)) {
          navigate(companyPath('/locked'), { replace: true });
          return;
        }
        setError(err);
      });
  }, [token, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
          <SignInBrandLogo className="mb-3" />
          <div className="brand-wordmark text-xl" style={{ color: 'var(--brand-primary)' }}>
            Safety<span className="accent">&amp;</span>Compliance
          </div>
          <div className="mt-1">
            <PoweredByFooter />
          </div>
          <p className="mt-4 text-sm text-slate-500">
            We couldn’t process your invitation link. It may have expired or been used already.
          </p>
          {/* A dead link was previously the end of the road here — this branch
              offered no action at all (the driver one not even a contact
              address). Links are single-use, so "ask for another" is the fix
              rather than "ask a person". */}
          <div className="mt-4">
            <Link
              to={companyPath('/login')}
              className="inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold text-white"
              style={{ background: 'var(--brand-primary)' }}
            >
              Email me a new sign-in link
            </Link>
          </div>
          <div className="mt-4">
            <ErrorMessage error={error} />
          </div>
          <div className="mt-6">
            <AppVersion />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50">
      <CenteredSpinner />
      <p className="text-sm text-slate-500">Preparing your sign-in…</p>
    </div>
  );
}
